import type { RefObject } from 'react'
import { useEffect, useState } from 'react'

import { noop } from '@app/utils/noop'

const elToCallbackMap = new WeakMap<Element, (entry: IntersectionObserverEntry) => unknown>()

const observer = IS_BROWSER
  ? new window.IntersectionObserver(entries => {
      entries.forEach(entry => {
        const cb = elToCallbackMap.get(entry.target)
        if (cb) cb(entry)
      })
    })
  : {
      observe: noop,
      unobserve: noop,
    }

/** Returns whether given ref is in viewport */
export const useInView = (ref: RefObject<Element>, initial = !IS_BROWSER) => {
  const [inView, setInView] = useState(initial)

  useEffect(() => {
    const cb = (entry: IntersectionObserverEntry) => {
      setInView(entry.isIntersecting)
    }

    const el = ref.current
    if (!el) return

    elToCallbackMap.set(el, cb)
    observer.observe(el)

    return () => {
      elToCallbackMap.delete(el)
      observer.unobserve(el)
    }
  }, [ref])

  return inView
}

/** Returns whether given ref is in viewport */
export const useInViewEffect = (ref: RefObject<Element | null>, handler: (intersecting: boolean) => void, deps: any[]) => {
  useEffect(() => {
    const cb = (entry: IntersectionObserverEntry) => {
      handler(entry.isIntersecting)
    }

    const el = ref.current
    if (!el) return

    elToCallbackMap.set(el, cb)
    observer.observe(el)

    return () => {
      elToCallbackMap.delete(el)
      observer.unobserve(el)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}
